import { useEffect } from 'react'
import { embedDashboard } from "@superset-ui/embedded-sdk";
import Toast from "@/components/Toast";
import useToast from "@/hooks/use-toast";
import supersetDashboardApi from '@/services/api/superset';
import { jwtDecode } from 'jwt-decode';
import usePage from '@/hooks/use-page';
import Breadcrumbs from '@/componentsDS/Breadcrumbs/index.jsx';
import { useFeature } from '@/hooks/use-feature/index'
import useDashboard from '@/hooks/use-dashboard/index';
import { useParams } from '@/lib/router-dom/index';
import { featureConstant } from '@/constants/configuration/feature';
import { useSelector } from 'react-redux';

const DashboardPage = () => {
	const {getCache, isCacheValid, updateTabCache} =useDashboard()
	const  {tabId}= useParams()
	const isEmbedDashboard = useFeature(featureConstant.DASHBOARD_EMBEDDING)
	const profile  = useSelector(state => state.auth.profile)

	// to fetch embed id of each dashboard
	const fetchEmbedId = async (id) => {
		if (!id) return
		try {
			const [error, response] = await supersetDashboardApi.getEmbedId(id)
			if (error) {
				throw new Error(error.message)
			}
			return response

		} catch (error) {
			throw new Error(`Error fetching guest token: ${error.message}`);
		}
	}

	const tabGuestToken = async () => {
		const cachedTab = getCache()
		if (!isCacheValid(tabId)) {
			return updateTabCache(tabId, (await fetchEmbedId(tabId)))
		}
		return cachedTab[tabId]
	}

	useEffect(() => {
		if(!isEmbedDashboard){
			embedDashboardInUI();
		}
	}, [])

	// to render dashboard only when tabId exist to get correct dashboard token

	useEffect(() => {
		if(tabId){
				embedDashboardInUI();
		}
	}, [tabId])

	useEffect(() => {
		const dashboardContainer = document.getElementById('superset-dashboard');

		let isHovering = false;
		let intervalId;

		const handleMouseEnter = () => {
			isHovering = true;
			intervalId = setInterval(() => {
				if (isHovering) {
					window.dispatchEvent(new Event('dashboard-activity'));
				}
			}, 60000);
		};

		const handleMouseLeave = () => {
			isHovering = false;
			clearInterval(intervalId);
		};

		if (dashboardContainer) {
			dashboardContainer.addEventListener('mouseenter', handleMouseEnter);
			dashboardContainer.addEventListener('mouseleave', handleMouseLeave);
		}

		return () => {
			if (dashboardContainer) {
				dashboardContainer.removeEventListener('mouseenter', handleMouseEnter);
				dashboardContainer.removeEventListener('mouseleave', handleMouseLeave);
			}
			clearInterval(intervalId);
		};
	}, []);



	const { breadcrumbsItems, setBreadcrumbsItems } = usePage({
		title: 'Dashboard',
		breadcrumbs: [
			{
				label: '',
				link: `/`,
				isHomePage: true
			}
		]
	 })

	const { toastList, addToastMessage } = useToast();

	useEffect(() => {
		if(isEmbedDashboard){

			const tabIndex = profile?.dashboard?.findIndex(tab => tab.id === Number(tabId));
			setBreadcrumbsItems(() => [
				{
					label: "",
					link: '/',
					isHomePage: tabIndex === 0
				},
				{
					label: profile?.dashboard[tabIndex]?.dashboard_title,
					link: '',
				}
			])
		}
	}, [tabId])

	function getTokenExpiration(token) {
		try {
			if (!token) {
				return 0;
			}
			const decoded = jwtDecode(token);
			return decoded.exp * 1000;
		} catch (error) {
			console.error('Error decoding token', error.message);
			return 0;
		}
	}

	function cacheResponse(response) {
		sessionStorage.setItem('cachedSupersetToken', response.token);
		sessionStorage.setItem('cachedSupersetDomain', response.domain);
		sessionStorage.setItem('cachedDashboardId', response.dashboardId);
		return response;
	}

	async function getGuestToken() {
		if (getTokenExpiration(sessionStorage.getItem('cachedSupersetToken')) < Date.now()) {
			return cacheResponse(await fetchGuestTokenFromBackend());
		}
		return {
			token: sessionStorage.getItem('cachedSupersetToken'),
			domain: sessionStorage.getItem('cachedSupersetDomain'),
			dashboardId: sessionStorage.getItem('cachedDashboardId')};
	}

	async function fetchGuestTokenFromBackend() {
		try {
			const response = await supersetDashboardApi.index();
			if(response[1]) {
				return response[1];
			} else if(response[0]) {
				throw new Error(response[0].message || JSON.stringify(response[0]));
			}
			throw new Error('Error processing response from backend');
		} catch (error) {
			console.error('Error fetching guest token', error.message);
			throw new Error(`Error fetching guest token: ${error.message}`);
		}
	}


	async function embedDashboardInUI() {
		try {
			let data={};
			if(isEmbedDashboard){
				const { uuid, guestToken, domain}=await tabGuestToken()
				data.id= uuid
				data.token=guestToken
				data.domain=domain
			}else {
				const {domain, token, dashboardId} =await getGuestToken()
				data.domain=domain
				data.token=token
				data.id=dashboardId
			}
			embedDashboard({
				id: data.id,
				supersetDomain: data.domain,
				mountPoint: document.getElementById("superset-dashboard"),
				fetchGuestToken: () => data.token,
				dashboardUiConfig: {
						hideTitle: false,
						filters: {
								expanded: true,
						}
				},
			});
		} catch (err) {
			addToastMessage(
				 err.message, 'error'
			);
		}

	}

	return (
		<>
			<Toast toastList={toastList} />
			<Breadcrumbs breadcrumbItems={breadcrumbsItems} />
			<div id='superset-dashboard' data-testid="superset-dashboard">
			</div>
		</>
	)
}

export default DashboardPage
